import * as React from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { Text } from '@/ui';
import type { ChipType } from '@/ui/chip';
import { Chip } from '@/ui/chip';

export type RefundMethodOptionRow = {
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  amount: string;
};

type RefundMethodOptionProps = {
  title: string;
  chipText: string;
  chipType: ChipType;
  rows: RefundMethodOptionRow[];
  active?: boolean;
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
};

export function RefundMethodOption({
  title,
  chipText,
  chipType,
  rows,
  active = false,
  onPress,
  style,
}: RefundMethodOptionProps) {
  const { styles } = useStyles(stylesheet);
  const [hover, setHover] = React.useState(false);

  const containerStyle = active
    ? styles.active
    : hover
    ? styles.hover
    : styles.default;

  const content = (
    <View style={styles.content}>
      <View style={styles.header}>
        <Text type="bodyBold">{title}</Text>

        <Chip type={chipType} text={chipText} />
      </View>

      {rows.map((row) => (
        <View key={row.label} style={styles.row}>
          {row.icon}

          <View style={styles.rowTextGroup}>
            <Text type="caption1">{row.label}</Text>

            <Text type="caption2" style={styles.textSecondary}>
              {row.sublabel}
            </Text>
          </View>

          <Text type="bodyBold">{row.amount}</Text>
        </View>
      ))}
    </View>
  );

  if (!onPress) {
    return (
      <View style={[styles.container, containerStyle, style]}>{content}</View>
    );
  }

  return (
    <Pressable
      style={[styles.container, containerStyle, style]}
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      hitSlop={8}
      onPress={onPress}
    >
      {content}
    </Pressable>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    width: '100%',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
  },
  content: {
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  rowTextGroup: {
    flex: 1,
    gap: 4,
  },
  textSecondary: {
    color: theme.colors.text.secondary,
  },
  default: {
    backgroundColor: theme.colors.card.default.backgroundColor,
    borderColor: theme.colors.card.default.borderColor,
  },
  active: {
    backgroundColor: theme.colors.card.active.backgroundColor,
    borderColor: theme.colors.card.active.borderColor,
  },
  hover: {
    backgroundColor: theme.colors.card.hover.backgroundColor,
    borderColor: theme.colors.card.hover.borderColor,
  },
}));
